import React, { ReactElement, useEffect, useState } from "react";
import "primeicons/primeicons.css";
import { Sidebar } from "primereact/sidebar";
import { Galleria } from "primereact/galleria";
import { ImageAlbumProps, ImageAlbum } from "../ImageAlbum/ImageAlbum";
import "./ImageAlbumGallery.css";
import { Button } from "primereact/button";
import { useTranslation } from "react-i18next";
import { successToast, warningToast } from "../Toasts";
import { TFunction } from "i18next";

export interface ImageAlbumGalleryProps {
    zipUrl: string;
    albums: ImageAlbumProps[];
    defaultAlbum: ImageAlbumProps;
    selectedAlbumKey?: string;
    openButtonStyle?: "default" | "icon-only";
    autoOpen: boolean;
    closeCallback?: () => void;
    downloadDisabled?: boolean;
    earlyRelease?: boolean;
    downloadCallback?: () => void;
}

const downloadFile = (url: string) => {
    const a = document.createElement("a");
    a.style.display = "none";
    a.href = url;

    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
};

function AddFrontImageToAlbumImagesList(album: ImageAlbumProps) {
    if (!album.images) {
        album.images = [];
    }

    album.images.unshift(album.frontImage);
    // Ensure no duplication of cover image from inserting it here, or in general
    album.images = Array.from(new Set(album.images));
}

const handleAlbumChange = (
    album: ImageAlbumProps,
    setCurrentAlbum: any,
    setActiveIndex: any
) => {
    setCurrentAlbum(album);
    setActiveIndex(0);
};

const AlbumComponent = (
    album: ImageAlbumProps,
    setCurrentAlbum: any,
    currentAlbum: ImageAlbumProps,
    setActiveIndex: any
) => {
    const isActiveAlbum =
        album.uniqueAlbumNumber === currentAlbum.uniqueAlbumNumber;

    let imageWrapperClasses = "";
    if (isActiveAlbum) {
        imageWrapperClasses += "active-album";
    } else {
        imageWrapperClasses += "inactive-album";
    }

    return (
        <div
            className={"md:w-full md:pb-0 md:p-3 p-2 album-child"}
            onClick={() =>
                handleAlbumChange(album, setCurrentAlbum, setActiveIndex)
            }
            key={album.frontImage}
            style={{ cursor: "pointer" }}
        >
            <ImageAlbum
                key={album.frontImage}
                frontImage={album.frontImage}
                albumName={album.albumName}
                wrapperClasses={imageWrapperClasses}
            />
        </div>
    );
};

async function downloadItem(
    url: string,
    t: TFunction,
    earlyRelease?: boolean,
    downloadCallback?: () => void
) {
    try {
        if (earlyRelease) {
            downloadCallback && downloadCallback();
            return;
        }

        successToast(t("download.downloadStarted"));
        const response = await fetch(url);
        if (!response || response.status !== 200) {
            console.error(response.statusText + " - " + url);
            warningToast(t("download.downloadFailed"));
            return;
        }

        downloadFile(url);
    } catch (error) {
        console.error(error);
        warningToast(t("download.downloadFailed"));
    }
}

export const ImageAlbumGallery = (
    props: ImageAlbumGalleryProps
): ReactElement => {
    const albums = props.albums;
    const defaultAlbum = props.defaultAlbum;
    const { t } = useTranslation("common");

    albums.forEach((album) => {
        AddFrontImageToAlbumImagesList(album);
    });
    AddFrontImageToAlbumImagesList(defaultAlbum);

    const [visible, setVisible] = useState(false);
    const [currentAlbum, setCurrentAlbum] = useState(
        albums.filter((alb) => alb.albumName === props.selectedAlbumKey)[0] ??
            defaultAlbum
    );
    const [activeIndex, setActiveIndex] = useState(0);

    const itemTemplate = (_src: string | undefined) => {
        // Hard set this to handle a bug whereby src is sometimes undefined, seems to be an issue with galleria
        // (or with how it's being used here) similar to https://github.com/primefaces/primereact/issues/3973. It's either this
        // or rearchitect this component to (maybe) solve, so going with this approach for now.
        // Active index gets set to 0 each time an album is clicked, in handleAlbumChange().
        return (
            <img
                src={currentAlbum.images![activeIndex]}
                style={{ width: "100%" }}
            />
        );
    };

    const showNavigators = currentAlbum.images!.length > 1;

    const openGallery = (currentAlbum: ImageAlbumProps) => {
        setVisible(true);
        if (
            currentAlbum.albumName !== props.selectedAlbumKey &&
            currentAlbum.albumName !== defaultAlbum.albumName
        ) {
            setCurrentAlbum(
                albums.filter(
                    (alb) => alb.albumName === props.selectedAlbumKey
                )[0] ?? defaultAlbum
            );
        }

        setTimeout(() => {
            const albumElement =
                document.getElementsByClassName("active-album");
            albumElement[0]?.scrollIntoView({
                block: "center",
                inline: "nearest"
            });
        }, 25);
    };

    useEffect(() => {
        if (props.autoOpen) {
            openGallery(props.defaultAlbum);
        }
    }, [props.autoOpen]);

    const closeViewer = () => {
        if (props.closeCallback) {
            props.closeCallback();
        } else {
            setVisible(false);
        }
    };

    const tooltipKey = props.selectedAlbumKey
        ? "imageAlbumGallery.selectedAlbumImages"
        : "imageAlbumGallery.tooltip";

    return (
        <>
            <Button
                id={"ImageGalleryOpenIcon"}
                icon={"pi pi-images"}
                className={`ml-3 p-button-rounded ${
                    props.openButtonStyle === "icon-only"
                        ? "p-button-text"
                        : "p-button-secondary image-gallery-open-icon"
                }`}
                aria-label="Image Gallery"
                onClick={() => {
                    openGallery(currentAlbum);
                }}
                tooltip={t(tooltipKey)}
                tooltipOptions={{ position: "left" }}
            ></Button>
            <Sidebar
                id={"ImageAlbumGallerySidebar"}
                showCloseIcon={true}
                visible={visible}
                fullScreen={true}
                onHide={closeViewer}
            >
                <div
                    id={"ImageAlbumGalleryScrollPanel"}
                    className={
                        "md:top-0 left-0 md:w-15rem w-full absolute bottom-0"
                    }
                >
                    {AlbumComponent(
                        defaultAlbum,
                        setCurrentAlbum,
                        currentAlbum,
                        setActiveIndex
                    )}
                    {albums.map((album) =>
                        AlbumComponent(
                            album,
                            setCurrentAlbum,
                            currentAlbum,
                            setActiveIndex
                        )
                    )}
                </div>
                <div className={"image-album-gallery-wrapper"}>
                    <Galleria
                        value={currentAlbum.images}
                        numVisible={currentAlbum.images!.length}
                        activeIndex={activeIndex}
                        onItemChange={(e) => setActiveIndex(e.index)}
                        circular
                        showItemNavigators={showNavigators}
                        showThumbnails={false}
                        item={itemTemplate}
                        className={"album-galleria"}
                    />
                    <div className={"download-wrapper"}>
                        {props.downloadDisabled ? null : (
                            <Button
                                icon={"pi pi-download"}
                                className={"p-button-rounded p-button-text"}
                                onClick={async () => {
                                    await downloadItem(
                                        props.zipUrl,
                                        t,
                                        props.earlyRelease,
                                        props.downloadCallback
                                    );
                                }}
                            ></Button>
                        )}
                    </div>
                </div>
            </Sidebar>
        </>
    );
};
